import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mdvip/shared/components/ui/select';
import { useAbility } from '@mdvip/shared/stores/authStore';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { canAccessView, type DatasetKey, VIEWS } from '@/lib/member-views/views';

export interface ViewSelectProps {
  /** The view on screen. */
  value: DatasetKey;
}

/**
 * ViewSelect — picks the directory's view. Every view the user may see is listed; the ones not
 * built yet are disabled and marked "Coming soon". A view behind a permission they don't hold
 * isn't listed at all — "Coming soon" would promise something they'll never get.
 *
 * The choice lives in the URL's `?view=`, so it survives a reload and can be linked to. The page
 * reads it back and mounts the chosen view fresh, which is what resets its page, letter and sort.
 */
export function ViewSelect({ value }: ViewSelectProps) {
  const { t } = useTranslation('providers');
  const [, setSearchParams] = useSearchParams();
  const id = useId();
  const ability = useAbility();
  const views = VIEWS.filter((view) => canAccessView(view, ability));
  const labelOf = (key: DatasetKey) => {
    const view = VIEWS.find((entry) => entry.key === key);
    return view ? t(view.labelKey) : key;
  };

  return (
    <div className="@3xl/md:w-72">
      <label htmlFor={id} className="sr-only">
        {t('memberViews.toolbar.viewLabel')}
      </label>
      <Select<DatasetKey>
        value={value}
        onValueChange={(next) => {
          if (next === null || next === value) return;
          setSearchParams((params) => {
            params.set('view', next);
            return params;
          });
        }}
      >
        <SelectTrigger id={id} className="h-11 w-full">
          <SelectValue>{(key: DatasetKey) => labelOf(key)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {views.map((view) => (
            <SelectItem key={view.key} value={view.key} disabled={!view.available}>
              {t(view.labelKey)}
              {!view.available && (
                <span className="ml-auto text-xs text-muted-foreground">{t('memberViews.comingSoon')}</span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
